import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  printPurchaseOrder
} from '../utils/poPrint';
const isJuteFabric = name => /\bjute\b/i.test(String(name||'')) && !/\blabels?\b/i.test(String(name||''));
const isYards = unit => /^(yards?|yds?)$/i.test(String(unit||'').trim());
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const STATUS_COLORS = {
  draft:     { color:'#8A8A8A', bg:'#F0F0F0' },
  approved:  { color:'#1E4FAD', bg:'#EDF4FF' },
  received:  { color:'#2E6B3A', bg:'#E8F5EB' },
  cancelled: { color:'#8F2E24', bg:'#FDEAEA' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{display:'inline-flex',padding:'3px 10px',borderRadius:20,
      fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',
      background:s.bg,color:s.color}}>
      {(status||'').replace('_',' ')}
    </span>
  );
};

export default function PurchaseOrders() {
  const { user, hasPermission } = useAuth();
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [viewPO, setViewPO] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);

  const canCreate =
    [
      'super_admin',
      'admin',
      'inventory_officer',
      'sales_manager',
      'content_sales',
      'front_desk_officer'
    ].includes(user?.role) ||
    hasPermission(
      'purchase_orders:create'
    );
  const canApprove = user?.role === 'super_admin';
  const canEditDelete =
    user?.role === 'super_admin';
  const canSend = [
    'super_admin',
    'admin',
    'inventory_officer',
    'sales_manager'
  ].includes(user?.role);

  const load = () => {
    setLoading(true);
    const qs = filter ? '?status='+filter : '';
    get('/purchase-orders'+qs).then(d => setPOs(d.data||[])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    get('/suppliers').then(d => setSuppliers(d.data||[]));
    get('/materials?include_supplies=true').then(d => setMaterials(d.data||[]));
    get('/products').then(d => setProducts(d.data||[]));
  }, [filter]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this Purchase Order?')) return;
    try { await put('/purchase-orders/'+id+'/approve', {}); load(); }
    catch (err) { alert(err.message); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this Purchase Order?')) return;
    try { await put('/purchase-orders/'+id+'/cancel', {}); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Purchase Orders</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Manage supplier purchase orders</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New PO</button>
        )}
      </div>

      {/* Status filter */}
      <div className="search-bar" style={{marginBottom:16,flexWrap:'wrap'}}>
        {['','draft','approved','received','cancelled'].map(s => (
          <button key={s} className={'btn btn-sm '+(filter===s?'btn-primary':'btn-outline')}
            onClick={() => setFilter(s)}>
            {s===''?'All':s.charAt(0).toUpperCase()+s.slice(1)}
          </button>
        ))}
      </div>

      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead>
              <tr>
                <th>PO Number</th><th>Date</th><th>Supplier</th>
                <th>Expected</th><th style={{textAlign:'right'}}>Total</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pos.length === 0
                ? <tr><td colSpan="7"><div className="empty-state"><h3>No purchase orders yet</h3></div></td></tr>
                : pos.map(po => (
                  <tr key={po.id}>
                    <td style={{fontWeight:500}}>{po.po_number}</td>
                    <td>{po.date}</td>
                    <td>{po.supplier_name}</td>
                    <td>{po.expected_date||'—'}</td>
                    <td className="amt">{fmt(po.total)}</td>
                    <td><StatusBadge status={po.status} /></td>
                    <td>
                      <div className="flex-center gap-sm">
                        <button className="btn btn-ghost btn-sm" onClick={() => setViewPO(po)}>View</button>
                        {canApprove && po.status==='draft' && (
                          <button className="btn btn-gold btn-sm" onClick={() => handleApprove(po.id)}>Approve</button>
                        )}
                        {canEditDelete && (po.status==='draft'||po.status==='approved') && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={async () => {
                              try {
                                const fullPO =
                                  await get(
                                    '/purchase-orders/' +
                                    po.id
                                  );

                                setEditingPO(fullPO);
                              } catch (error) {
                                alert(
                                  error.message ||
                                  'The Purchase Order could not be loaded for editing'
                                );
                              }
                            }}
                          >
                            Edit
                          </button>
                        )}
                        {canEditDelete && po.status!=='received' && (
                          <button className="btn btn-danger btn-sm" onClick={async () => {
                            if (!window.confirm('Delete this PO?')) return;
                            try { await del('/purchase-orders/'+po.id); load(); }
                            catch (err) { alert(err.message); }
                          }}>Delete</button>
                        )}
                        {canApprove && po.status==='draft' && (
                          <button className="btn btn-outline btn-sm" style={{color:'var(--unpaid)',borderColor:'var(--unpaid)'}}
                            onClick={() => handleCancel(po.id)}>Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreatePOModal
          suppliers={suppliers} materials={materials} products={products}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }} />
      )}

      {editingPO && (
        <CreatePOModal
          suppliers={suppliers}
          materials={materials}
          products={products}
          initialPO={editingPO}
          onClose={() =>
            setEditingPO(null)
          }
          onSaved={result => {
            setEditingPO(null);
            load();

            if (
              result &&
              result.requires_reapproval
            ) {
              alert(
                'The revised Purchase Order is now a draft. It must be approved again before sharing it with the supplier.'
              );
            }
          }}
        />
      )}

      {viewPO && (
        <ViewPOModal
          poId={viewPO.id}
          canApprove={canApprove}
          canReceive={['super_admin','admin'].includes(user?.role)}
          canSend={canSend}
          onClose={() => setViewPO(null)}
          onUpdated={() => { setViewPO(null); load(); }} />
      )}
    </div>
  );
}

function CreatePOModal({
  suppliers,
  materials,
  products,
  initialPO = null,
  onClose,
  onSaved
}) {
  const {user,refreshUser}=useAuth();
  useEffect(()=>{refreshUser().catch(()=>{});},[refreshUser]);
  const directApproval=user?.role==='super_admin';
  const isEditing =
    Boolean(initialPO && initialPO.id);

  const [form, setForm] = useState(
    isEditing
      ? {
          supplier_id:
            initialPO.supplier_id || '',
          date:
            String(
              initialPO.date || ''
            ).slice(0, 10),
          expected_date:
            String(
              initialPO.expected_date ||
              ''
            ).slice(0, 10),
          notes:
            initialPO.notes || ''
        }
      : {
          supplier_id: '',
          date:
            new Date()
              .toISOString()
              .split('T')[0],
          expected_date: '',
          notes: ''
        }
  );

  const [approvedRequests,setApprovedRequests]=useState([]);
  const [requestId,setRequestId]=useState(initialPO?.purchase_request_id||'');
  const [linkRequest,setLinkRequest]=useState(Boolean(initialPO?.purchase_request_id));
  const [requestLoading,setRequestLoading]=useState(true);
  const [requestError,setRequestError]=useState('');
  useEffect(()=>{
    let active=true;
    const suffix=initialPO?.id?'?exclude_po_id='+encodeURIComponent(initialPO.id):'';
    get('/purchase-orders/available-requests'+suffix)
      .then(d=>{if(active)setApprovedRequests(d.data||[]);})
      .catch(e=>{if(active)setRequestError(e.message);})
      .finally(()=>{if(active)setRequestLoading(false);});
    return ()=>{active=false;};
  },[initialPO?.id]);
  const selectedRequest=approvedRequests.find(x=>x.id===requestId);
  const requestItems=selectedRequest?.available_items||[];
  const availableQuantity=id=>requestItems.find(i=>i.material_id===id)?.remaining_quantity;
  const permittedMaterials=materials.filter(x=>x.is_active&&(!isJuteFabric(x.name)||isYards(x.unit_of_measure))&&
    ((directApproval&&!requestId)||requestItems.some(i=>i.material_id===x.id&&Number(i.remaining_quantity)>0)));
  const [lines, setLines] = useState(
    isEditing &&
    Array.isArray(initialPO.lines) &&
    initialPO.lines.length > 0
      ? initialPO.lines.map(line => ({
          item_type:
            line.item_type ||
            'material',
          material_id:
            line.material_id || '',
          product_id:
            line.product_id || '',
          description:
            line.description || '',
          quantity:
            line.quantity,
          unit_cost:
            line.unit_cost
        }))
      : [
          {
            item_type: 'material',
            material_id: '',
            product_id: '',
            description: '',
            quantity: 1,
            unit_cost: 0
          }
        ]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const total = lines.reduce((sum,l) => sum+(parseFloat(l.quantity||0)*parseFloat(l.unit_cost||0)), 0);

  const updateLine = (i, field, val) => {
    const updated = [...lines];
    updated[i] = field==='item_type'?{...updated[i],item_type:val,material_id:'',product_id:'',description:''}:{...updated[i], [field]:val};
    if (field==='material_id' && val) {
      const m = materials.find(x => x.id===val);
      if (m) { updated[i].description = m.name; updated[i].unit_cost = parseFloat(m.weighted_avg_cost)||0; }
    }
    if (field==='product_id' && val) {
      const p = products.find(x => x.id===val);
      if (p) { updated[i].description = p.name+(p.size?' ('+p.size+')':''); updated[i].unit_cost = parseFloat(p.selling_price)||0; }
    }
    setLines(updated);
  };

  const submit = async () => {
    if(directApproval&&linkRequest&&!requestId){setError('Choose an available request or switch back to direct purchase.');return;}
    if (!form.supplier_id) { setError('Select a supplier'); return; }
    if (lines.some(l=>!['material','product'].includes(l.item_type)||(l.item_type==='material'?!l.material_id:!l.product_id))) {setError('Select registered items for every line.');return;}
    if(!directApproval&&lines.some(l=>l.item_type==='material')&&!requestId){setError('Select the approved material purchase request.');return;}
    if(requestId&&(!selectedRequest||lines.some(l=>l.item_type==='material'&&
      (!requestItems.some(i=>i.material_id===l.material_id)||Number(l.quantity)>Number(availableQuantity(l.material_id)))))){
      setError('This request has insufficient quantity remaining. Reopen the form to refresh availability.');return;
    }
    if (!lines.some(l => l.description)) { setError('Add at least one item'); return; }
    const juteLines=lines.filter(l=>l.item_type==='material'&&isJuteFabric(materials.find(m=>m.id===l.material_id)?.name));
    if(juteLines.some(l=>!isYards(materials.find(m=>m.id===l.material_id)?.unit_of_measure))){setError('Select the yards catalogue entry for jute fabric.');return;}
    if(juteLines.length&&!window.confirm('Confirm these jute quantities are YARDS and the unit costs are PRICES PER YARD:\n'+juteLines.map(l=>`${l.description}: ${l.quantity} yards at ${fmt(l.unit_cost)} per yard`).join('\n'))){return;}
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_request_id:requestId||null,
        lines: lines.filter(
          line =>
            String(
              line.description ||
              ''
            ).trim()
        ).map(line=>({...line,unit:line.item_type==='material'?materials.find(m=>m.id===line.material_id)?.unit_of_measure:undefined,jute_yards_confirmed:true}))
      };

      if (
        isEditing &&
        initialPO.status === 'approved' &&
        !window.confirm(
          directApproval?'Saving will revoke the previous supplier link and approve the revised PO with your current signature. Continue?':'Editing this approved Purchase Order will revoke its active supplier link, remove the existing approval and return it to draft. Continue?'
        )
      ) {
        setSaving(false);
        return;
      }

      const result =
        isEditing
          ? await put(
              '/purchase-orders/' +
                initialPO.id,
              payload
            )
          : await post(
              '/purchase-orders',
              payload
            );

      onSaved(result);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:700}}>
        <div className="modal-hdr">
          <span className="modal-title">
            {isEditing
              ? 'Edit Purchase Order'
              : 'New Purchase Order'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Supplier *</label>
                <select className="form-control" value={form.supplier_id} onChange={e => setForm({...form,supplier_id:e.target.value})}>
                  <option value="">Select supplier</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">PO Date *</label>
                <input type="date" className="form-control" value={form.date} onChange={e => setForm({...form,date:e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Expected Delivery Date</label>
              <input type="date" className="form-control" value={form.expected_date} onChange={e => setForm({...form,expected_date:e.target.value})} />
            </div>
          </div>

          <div className="flex-between mb-md">
            <span style={{fontWeight:500,fontSize:13}}>Line Items</span>
            <button className="btn btn-outline btn-sm"
              onClick={() => setLines([...lines,{item_type:'material',material_id:'',product_id:'',description:'',quantity:1,unit_cost:0}])}>
              + Add Item
            </button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1.2fr 1.5fr 2fr 0.8fr 1.2fr auto',gap:6,marginBottom:6}}>
            <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Type</div>
            <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Item</div>
            <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Description</div>
            <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Qty</div>
            <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Unit Cost</div>
            <div></div>
          </div>

          <div className="form-group">
            {directApproval&&<>
              <p style={{fontWeight:700}}>Direct purchase — no inventory request required.</p>
              <label><input type="checkbox" checked={linkRequest} onChange={e=>{
                setLinkRequest(e.target.checked);setRequestId('');
                setLines(lines.map(l=>l.item_type==='material'?{...l,material_id:'',description:''}:l));
              }}/> Link an approved purchase request instead (optional)</label>
            </>}
            {(!directApproval||linkRequest)&&<>
              <label className="form-label">Approved purchase request</label>
              <select className="form-control" value={requestId} disabled={requestLoading} onChange={e=>{
                setRequestId(e.target.value);
                setLines(lines.map(l=>l.item_type==='material'?{...l,material_id:'',description:''}:l));
              }}>
                <option value="">{requestLoading?'Loading available requests…':'Choose a request with quantity remaining…'}</option>
                {approvedRequests.map(p=><option key={p.id} value={p.id}>{p.request_number} — {p.title}</option>)}
              </select>
              <p>Fully allocated items are hidden. Draft and approved POs reserve quantities; cancelled POs release them.</p>
              {!requestLoading&&!requestError&&!approvedRequests.length&&<p>No approved requests have registered materials remaining to order.</p>}
              {selectedRequest&&<ul>{requestItems.map(i=><li key={i.material_id}>{i.description}: {i.remaining_quantity} {i.unit||'units'} remaining</li>)}</ul>}
              {requestError&&<p role="alert">Could not load available requests: {requestError}</p>}
            </>}
          </div>
          {lines.map((line,i) => (
            <div key={i} style={{display:'grid',gridTemplateColumns:'1.2fr 1.5fr 2fr 0.8fr 1.2fr auto',gap:6,marginBottom:8,alignItems:'center'}}>
              <select className="form-control" style={{fontSize:12}} value={line.item_type}
                onChange={e => updateLine(i,'item_type',e.target.value)}>
                <option value="material">Material</option>
                <option value="product">Product</option>

              </select>
              {line.item_type==='material'
                ? <select className="form-control" style={{fontSize:12}} value={line.material_id}
                    onChange={e => updateLine(i,'material_id',e.target.value)}>
                    <option value="">Select material</option>
                    {permittedMaterials.filter(m=>m.id===line.material_id||!lines.some((other,idx)=>idx!==i&&other.material_id===m.id)).map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit_of_measure}){m.is_supply ? ' — Supplies / Spares' : ''}</option>)}
                  </select>
                : line.item_type==='product'
                  ? <select className="form-control" style={{fontSize:12}} value={line.product_id}
                      onChange={e => updateLine(i,'product_id',e.target.value)}>
                      <option value="">Select product</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  : <input className="form-control" style={{fontSize:12}} placeholder="Item name"
                      value={line.description} onChange={e => updateLine(i,'description',e.target.value)} />
              }
              <input className="form-control" style={{fontSize:12}} placeholder="Description"
                value={line.description} onChange={e => updateLine(i,'description',e.target.value)} />
              <label><span style={{fontSize:12,fontWeight:700}}>Quantity ({materials.find(m=>m.id===line.material_id)?.unit_of_measure||'units'})</span><input type="number" className="form-control" min="0.001" step="0.001" style={{fontSize:12}} max={requestId&&line.item_type==='material'?availableQuantity(line.material_id):undefined} value={line.quantity}
                onChange={e => updateLine(i,'quantity',e.target.value)} /></label>
              <label><span style={{fontSize:12,fontWeight:700}}>Price per {materials.find(m=>m.id===line.material_id)?.unit_of_measure||'unit'}</span><input type="number" className="form-control" min="0" step="0.001" style={{fontSize:12}} value={line.unit_cost}
                onChange={e => updateLine(i,'unit_cost',e.target.value)} /></label>
              <button className="btn btn-ghost btn-sm"
                onClick={() => setLines(lines.filter((_,idx) => idx!==i))}
                disabled={lines.length===1} style={{padding:8,color:'var(--ink-light)'}}>X</button>
            </div>
          ))}

          <div style={{display:'flex',justifyContent:'flex-end',marginTop:12,paddingTop:12,borderTop:'1px solid var(--border-light)'}}>
            <div style={{width:240,background:'#F2EFE7',borderRadius:8,padding:14}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0 0'}}>
                <span style={{fontWeight:700,fontSize:14,color:'#3B2A21'}}>Total</span>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:'#556B2F'}}>{fmt(total)}</span>
              </div>
            </div>
          </div>

          <div className="form-group" style={{marginTop:12}}>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving
              ? (
                  isEditing
                    ? 'Saving...'
                    : 'Creating...'
                )
              : (
                  isEditing
                    ? (directApproval?'Save & Approve Changes':'Save Changes')
                    : (directApproval?'Create & Approve PO':'Create Purchase Order')
                )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewPOModal({ poId, canApprove, canReceive, canSend, onClose, onUpdated }) {
  const [settings, setSettings] = React.useState({});
  const [po, setPO] = useState(null);
  const [receiving, setReceiving] = useState(false);
  const [sending, setSending] = useState(false);
  const [creatingSupplierLink, setCreatingSupplierLink] = useState(false);
  const [supplierShare, setSupplierShare] = useState(null);
  const [manualResponseLink, setManualResponseLink] = useState(null);
  const [recordingManualResponse, setRecordingManualResponse] = useState(false);
  const [manualResponseForm, setManualResponseForm] = useState({
    response: 'accepted',
    responder_name: '',
    responder_email: '',
    responder_phone: '',
    response_comment: '',
    evidence_note: ''
  });
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);

  const load = () => get('/purchase-orders/'+poId).then(setPO);
  useEffect(() => { get('/invoice-settings').then(d => setSettings(d||{})).catch(()=>{}); }, []);
  useEffect(() => { load(); }, [poId]);

  const handleApprove = async () => {
    if (!window.confirm('Approve this PO?')) return;
    try { await put('/purchase-orders/'+poId+'/approve', {}); load(); }
    catch (err) { alert(err.message); }
  };

  const handleReceive = () => {
    window.location.href =
      '/inventory-receipts?purchase_order_id=' +
      encodeURIComponent(poId);
  };

  const successfulDeliveries =
    (po?.deliveries || []).filter(
      delivery =>
        delivery.status === 'sent'
    );

  const latestSuccessfulDelivery =
    successfulDeliveries[0] || null;

  const handleSendToSupplier = async () => {
    if (!po.supplier_email) {
      alert(
        'This supplier has no email address. Add the email on the Suppliers page before sending this Purchase Order.'
      );
      return;
    }

    const hasBeenSent =
      successfulDeliveries.length > 0;

    const confirmation =
      hasBeenSent
        ? (
          'This Purchase Order was already emailed to ' +
          latestSuccessfulDelivery.recipient_email +
          '. Send it again to ' +
          po.supplier_email +
          '?'
        )
        : (
          'Email Purchase Order ' +
          po.po_number +
          ' to ' +
          po.supplier_email +
          '?'
        );

    if (!window.confirm(confirmation)) {
      return;
    }

    setSending(true);

    try {
      const result = await post(
        '/purchase-orders/' +
          poId +
          '/send-to-supplier',
        {
          resend: hasBeenSent
        }
      );

      alert(
        result.message ||
        'Purchase Order sent successfully'
      );

      await load();
    } catch (error) {
      alert(
        error.message ||
        'The Purchase Order could not be emailed'
      );
    } finally {
      setSending(false);
    }
  };

  const copySupplierMessage = async share => {
    const message =
      String(
        share &&
        share.share_message ||
        ''
      ).trim();

    if (!message) {
      throw new Error(
        'The secure sharing message is unavailable'
      );
    }

    try {
      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(
          message
        );

        alert(
          'The secure Purchase Order message has been copied.'
        );

        return;
      }
    } catch (_) {}

    window.prompt(
      'Copy this secure Purchase Order message:',
      message
    );
  };

  const handleCreateSupplierLink = async (
    deliveryChannel,
    copyAfterCreation = false
  ) => {
    if (creatingSupplierLink) {
      return null;
    }

    setCreatingSupplierLink(true);

    try {
      const result = await post(
        '/purchase-orders/' +
          poId +
          '/supplier-link',
        {
          delivery_channel:
            deliveryChannel,
          expires_in_days: 30
        }
      );

      const share =
        result && result.data
          ? result.data
          : result;

      if (
        !share ||
        !share.public_url ||
        !share.share_message
      ) {
        throw new Error(
          'The server did not return a complete secure link'
        );
      }

      setSupplierShare(share);

      if (copyAfterCreation) {
        await copySupplierMessage(share);
      }

      await load();

      return share;
    } catch (error) {
      alert(
        error.message ||
        'The secure Purchase Order link could not be created'
      );

      return null;
    } finally {
      setCreatingSupplierLink(false);
    }
  };

  const handleOpenWhatsApp = async () => {
    const rawPhone =
      String(
        po.supplier_phone ||
        ''
      ).replace(/\D/g, '');

    if (!rawPhone) {
      alert(
        'This supplier has no phone number. Add one on the Suppliers page or use Copy Link.'
      );

      return;
    }

    const phone =
      rawPhone.startsWith('0')
        ? '234' + rawPhone.slice(1)
        : rawPhone;

    if (
      phone.length < 7 ||
      phone.length > 15
    ) {
      alert(
        'The supplier phone number is invalid. Correct it on the Suppliers page first.'
      );

      return;
    }

    const share =
      await handleCreateSupplierLink(
        'whatsapp'
      );

    if (!share) {
      return;
    }

    const whatsappUrl =
      'https://wa.me/' +
      phone +
      '?text=' +
      encodeURIComponent(
        share.share_message
      );

    const whatsappWindow =
      window.open(
        whatsappUrl,
        '_blank',
        'noopener,noreferrer'
      );

    if (!whatsappWindow) {
      await copySupplierMessage(share);

      alert(
        'WhatsApp was blocked by the browser. The secure message has been prepared for you to copy.'
      );
    }
  };

  const openManualResponse = link => {
    if (
      !link ||
      link.status !== 'active' ||
      link.response
    ) {
      alert(
        'This supplier link cannot receive another response.'
      );

      return;
    }

    setManualResponseLink(link);

    setManualResponseForm({
      response: 'accepted',
      responder_name:
        link.recipient_name ||
        po.supplier_name ||
        '',
      responder_email:
        link.recipient_email ||
        po.supplier_email ||
        '',
      responder_phone:
        link.recipient_phone ||
        po.supplier_phone ||
        '',
      response_comment: '',
      evidence_note: ''
    });
  };

  const closeManualResponse = () => {
    if (recordingManualResponse) {
      return;
    }

    setManualResponseLink(null);
  };

  const handleRecordManualResponse = async event => {
    event.preventDefault();

    if (
      !manualResponseLink ||
      recordingManualResponse
    ) {
      return;
    }

    const responderName =
      String(
        manualResponseForm.responder_name ||
        ''
      ).trim();

    const responseComment =
      String(
        manualResponseForm.response_comment ||
        ''
      ).trim();

    const evidenceNote =
      String(
        manualResponseForm.evidence_note ||
        ''
      ).trim();

    if (responderName.length < 2) {
      alert(
        'Enter the name of the person responding for the supplier.'
      );

      return;
    }

    if (
      manualResponseForm.response !==
        'accepted' &&
      responseComment.length < 3
    ) {
      alert(
        manualResponseForm.response ===
          'changes_requested'
          ? 'Explain the changes requested by the supplier.'
          : 'Enter the supplier reason for declining the Purchase Order.'
      );

      return;
    }

    if (evidenceNote.length < 5) {
      alert(
        'Describe how and when the supplier provided this response.'
      );

      return;
    }

    const responseLabel = {
      accepted: 'Accepted',
      changes_requested:
        'Changes Requested',
      declined: 'Declined'
    }[manualResponseForm.response];

    if (
      !window.confirm(
        'Record this supplier response as "' +
        responseLabel +
        '"? This record cannot be edited or deleted.'
      )
    ) {
      return;
    }

    setRecordingManualResponse(true);

    try {
      const result = await post(
        '/purchase-orders/' +
          poId +
          '/supplier-links/' +
          manualResponseLink.id +
          '/manual-response',
        {
          response:
            manualResponseForm.response,
          responder_name:
            responderName,
          responder_email:
            String(
              manualResponseForm
                .responder_email ||
              ''
            ).trim(),
          responder_phone:
            String(
              manualResponseForm
                .responder_phone ||
              ''
            ).trim(),
          response_comment:
            responseComment,
          evidence_note:
            evidenceNote
        }
      );

      alert(
        result.message ||
        'Supplier response recorded successfully'
      );

      setManualResponseLink(null);
      await load();
    } catch (error) {
      alert(
        error.message ||
        'The supplier response could not be recorded'
      );
    } finally {
      setRecordingManualResponse(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this PO?')) return;
    try { await put('/purchase-orders/'+poId+'/cancel', {}); onUpdated(); }
    catch (err) { alert(err.message); }
  };

  if (!po) return (
    <div className="modal-overlay">
      <div className="modal" style={{textAlign:'center',padding:40}}><div className="spinner"></div></div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:680}}>
        <div className="modal-hdr">
          <div>
            <span className="modal-title">{po.po_number}</span>
            <span style={{marginLeft:10}}><StatusBadge status={po.status} /></span>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {canSend && po.status === 'approved' && (
              <button
                className="btn btn-gold btn-sm"
                onClick={handleSendToSupplier}
                disabled={sending}
                title={
                  po.supplier_email
                    ? 'Email a secure Purchase Order review link'
                    : 'Add the supplier email before sending a secure link'
                }
              >
                {sending
                  ? 'Sending...'
                  : successfulDeliveries.length > 0
                    ? 'Resend Secure Link'
                    : 'Email Secure Link'}
              </button>
            )}
            {canSend && po.status === 'approved' && (
              <button
                className="btn btn-outline btn-sm"
                onClick={handleOpenWhatsApp}
                disabled={creatingSupplierLink}
                title={
                  po.supplier_phone
                    ? 'Create and share a secure link through WhatsApp'
                    : 'Add the supplier phone number or use Copy Link'
                }
              >
                {creatingSupplierLink
                  ? 'Creating...'
                  : 'WhatsApp Link'}
              </button>
            )}

            {canSend && po.status === 'approved' && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() =>
                  handleCreateSupplierLink(
                    'copied_link',
                    true
                  )
                }
                disabled={creatingSupplierLink}
                title="Create and copy a secure supplier review link"
              >
                {creatingSupplierLink
                  ? 'Creating...'
                  : 'Copy Link'}
              </button>
            )}

            <button className="btn btn-outline btn-sm" onClick={() => printPurchaseOrder(
                po,
                po.lines || [],
                settings
              )}>🖨 Print / Save PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">

          {/* PO Info */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20,padding:14,background:'#F2EFE7',borderRadius:8}}>
            <div>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Supplier</div>
              <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{po.supplier_name}</div>
              {po.supplier_email ? (
                <div style={{fontSize:12,color:'#555',marginTop:4}}>
                  Email: {po.supplier_email}
                </div>
              ) : (
                <div style={{fontSize:12,color:'#8F2E24',marginTop:4,fontWeight:600}}>
                  Supplier email missing
                </div>
              )}
              {po.bank_name && <div style={{fontSize:12,color:'#555',marginTop:4}}>
                {po.bank_name} | {po.bank_account_no} | {po.bank_account_name}
              </div>}
            </div>
            <div>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Details</div>
              <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.8}}>
                <div><span style={{color:'#8A8A8A'}}>Date: </span>{po.date}</div>
                {po.expected_date && <div><span style={{color:'#8A8A8A'}}>Expected: </span>{po.expected_date}</div>}
                {po.approved_by_name && <div><span style={{color:'#8A8A8A'}}>Approved by: </span>{po.approved_by_name}</div>}
              </div>
            </div>
          </div>

          {/* Line items */}
          <table style={{width:'100%',borderCollapse:'collapse',marginBottom:16}}>
            <thead>
              <tr style={{background:'#556B2F'}}>
                <th style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Description</th>
                <th style={{padding:'9px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Type</th>
                <th style={{padding:'9px 12px',textAlign:'center',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Qty</th>
                <th style={{padding:'9px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Unit Cost</th>
                <th style={{padding:'9px 12px',textAlign:'right',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.08em'}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(po.lines||[]).map((l,i) => (
                <tr key={l.id} style={{background:i%2===0?'#fff':'#F2EFE7'}}>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontSize:13}}>{l.description}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}><span className="badge b-grey" style={{fontSize:9}}>{l.item_type}</span></td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center',fontSize:13}}>{Number(l.quantity).toLocaleString()}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontSize:13}}>{fmt(l.unit_cost)}</td>
                  <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontSize:13,fontWeight:600}}>{fmt(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <div style={{width:200,background:'#F2EFE7',borderRadius:8,padding:12}}>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span style={{fontWeight:700,fontSize:14,color:'#3B2A21'}}>Total</span>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color:'#556B2F'}}>{fmt(po.total)}</span>
              </div>
            </div>
          </div>

          {po.notes && (
            <div style={{padding:'10px 12px',background:'#F2EFE7',borderRadius:8,marginBottom:16,fontSize:13}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>Notes</div>
              {po.notes}
            </div>
          )}

            {(po.deliveries || []).length > 0 && (
              <div style={{
                marginBottom:16,
                padding:12,
                border:'1px solid #DDD6C8',
                borderRadius:8
              }}>
                <div style={{
                  fontSize:10,
                  fontWeight:700,
                  letterSpacing:'.08em',
                  textTransform:'uppercase',
                  color:'#8A8A8A',
                  marginBottom:8
                }}>
                  Email Delivery History
                </div>

                {(po.deliveries || []).map(
                  delivery => (
                    <div
                      key={delivery.id}
                      style={{
                        display:'flex',
                        justifyContent:'space-between',
                        gap:12,
                        padding:'7px 0',
                        borderTop:'1px solid #EEE8DC',
                        fontSize:12
                      }}
                    >
                      <div>
                        <strong>
                          {delivery.recipient_email}
                        </strong>

                        <div style={{
                          color:'#777',
                          marginTop:2
                        }}>
                          Requested by{' '}
                          {delivery.requested_by_name ||
                            'Unknown user'}
                        </div>
                      </div>

                      <div style={{
                        textAlign:'right'
                      }}>
                        <span style={{
                          fontWeight:700,
                          color:
                            delivery.status === 'sent'
                              ? '#2E6B3A'
                              : delivery.status === 'failed'
                                ? '#8F2E24'
                                : '#8B5E0A'
                        }}>
                          {delivery.status.toUpperCase()}
                        </span>

                        <div style={{
                          color:'#777',
                          marginTop:2
                        }}>
                          {new Date(
                            delivery.sent_at ||
                            delivery.requested_at
                          ).toLocaleString('en-NG')}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

          {supplierShare && (
            <div style={{
              marginBottom:16,
              padding:14,
              border:'1px solid #A8893D',
              borderRadius:8,
              background:'#FFF9E8'
            }}>
              <div style={{
                fontWeight:700,
                color:'#3B2A21',
                marginBottom:6
              }}>
                Secure Link Ready
              </div>

              <div style={{
                fontSize:12,
                color:'#665A4D',
                lineHeight:1.6,
                marginBottom:10
              }}>
                This link is shown only in this session.
                Copy it now. Creating another link will revoke
                the previous active link.
              </div>

              <div style={{
                padding:9,
                borderRadius:6,
                background:'#FFFFFF',
                border:'1px solid #DDD6C8',
                fontSize:11,
                overflowWrap:'anywhere',
                marginBottom:10
              }}>
                {supplierShare.public_url}
              </div>

              <button
                type="button"
                className="btn btn-gold btn-sm"
                onClick={() =>
                  copySupplierMessage(
                    supplierShare
                  )
                }
              >
                Copy Secure Message
              </button>
            </div>
          )}

          {(po.supplierLinks || []).length > 0 && (
            <section style={{
              marginBottom:18,
              border:'1px solid #DDD6C8',
              borderRadius:8,
              overflow:'hidden'
            }}>
              <div style={{
                padding:'11px 14px',
                background:'#3B2A21',
                color:'#F2EFE7',
                fontWeight:700,
                fontSize:13
              }}>
                Supplier Response History
              </div>

              <div style={{padding:12}}>
                {(po.supplierLinks || []).map(link => {
                  const isExpired =
                    link.status === 'active' &&
                    new Date(link.expires_at) <
                      new Date();

                  const displayStatus =
                    link.response ||
                    (isExpired
                      ? 'expired'
                      : link.status);

                  const statusDetails = {
                    accepted: {
                      label: 'Accepted',
                      color: '#2E6B3A',
                      background: '#EAF5EC'
                    },
                    changes_requested: {
                      label: 'Changes Requested',
                      color: '#8B5E0A',
                      background: '#FFF3D6'
                    },
                    declined: {
                      label: 'Declined',
                      color: '#8F2E24',
                      background: '#FBE9E7'
                    },
                    active: {
                      label: 'Awaiting Response',
                      color: '#355A7A',
                      background: '#EAF2F8'
                    },
                    responded: {
                      label: 'Response Recorded',
                      color: '#2E6B3A',
                      background: '#EAF5EC'
                    },
                    revoked: {
                      label: 'Revoked',
                      color: '#777',
                      background: '#EEEEEE'
                    },
                    expired: {
                      label: 'Expired',
                      color: '#777',
                      background: '#EEEEEE'
                    }
                  }[displayStatus] || {
                    label:
                      String(
                        displayStatus ||
                        'unknown'
                      ).replace(/_/g, ' '),
                    color: '#555',
                    background: '#EEEEEE'
                  };

                  return (
                    <div
                      key={link.id}
                      style={{
                        padding:12,
                        marginBottom:10,
                        border:'1px solid #E5DFD3',
                        borderRadius:7,
                        background:'#FFFFFF'
                      }}
                    >
                      <div style={{
                        display:'flex',
                        justifyContent:'space-between',
                        gap:10,
                        alignItems:'flex-start',
                        flexWrap:'wrap'
                      }}>
                        <div>
                          <div style={{
                            fontWeight:700,
                            fontSize:13,
                            color:'#3B2A21'
                          }}>
                            Link version {link.version_number}
                          </div>

                          <div style={{
                            marginTop:3,
                            color:'#777',
                            fontSize:11
                          }}>
                            Created {
                              new Date(
                                link.created_at
                              ).toLocaleString(
                                'en-NG'
                              )
                            } by {
                              link.created_by_name ||
                              'Authorized staff'
                            }
                          </div>
                        </div>

                        <span style={{
                          padding:'4px 9px',
                          borderRadius:20,
                          fontSize:10,
                          fontWeight:700,
                          color:statusDetails.color,
                          background:
                            statusDetails.background
                        }}>
                          {statusDetails.label}
                        </span>
                      </div>

                      <div style={{
                        display:'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit,minmax(140px,1fr))',
                        gap:8,
                        marginTop:10,
                        fontSize:11,
                        color:'#555'
                      }}>
                        <div>
                          <strong>Channel:</strong>{' '}
                          {String(
                            link.delivery_channel ||
                            ''
                          ).replace(/_/g, ' ')}
                        </div>

                        <div>
                          <strong>Views:</strong>{' '}
                          {Number(
                            link.view_count || 0
                          )}
                        </div>

                        <div>
                          <strong>Expires:</strong>{' '}
                          {new Date(
                            link.expires_at
                          ).toLocaleString('en-NG')}
                        </div>
                      </div>

                      {link.first_viewed_at && (
                        <div style={{
                          marginTop:7,
                          fontSize:11,
                          color:'#555'
                        }}>
                          First viewed:{' '}
                          {new Date(
                            link.first_viewed_at
                          ).toLocaleString('en-NG')}
                        </div>
                      )}

                      {link.response && (
                        <div style={{
                          marginTop:10,
                          padding:10,
                          borderRadius:6,
                          background:
                            statusDetails.background,
                          fontSize:12,
                          color:'#3B2A21'
                        }}>
                          <div>
                            <strong>Responded by:</strong>{' '}
                            {link.responder_name}
                          </div>

                          <div>
                            <strong>Source:</strong>{' '}
                            {String(
                              link.response_source ||
                              ''
                            ).replace(/_/g, ' ')}
                          </div>

                          {link.responded_at && (
                            <div>
                              <strong>Date:</strong>{' '}
                              {new Date(
                                link.responded_at
                              ).toLocaleString(
                                'en-NG'
                              )}
                            </div>
                          )}

                          {link.response_comment && (
                            <div style={{marginTop:6}}>
                              <strong>Comment:</strong>{' '}
                              {link.response_comment}
                            </div>
                          )}

                          {link.evidence_note && (
                            <div style={{marginTop:6}}>
                              <strong>
                                Staff evidence:
                              </strong>{' '}
                              {link.evidence_note}
                            </div>
                          )}
                        </div>
                      )}

                      {canSend &&
                       po.status === 'approved' &&
                       link.status === 'active' &&
                       !isExpired &&
                       !link.response && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          style={{marginTop:10}}
                          onClick={() =>
                            openManualResponse(link)
                          }
                        >
                          Record Manual Response
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {manualResponseLink && (
            <form
              onSubmit={
                handleRecordManualResponse
              }
              style={{
                marginBottom:18,
                padding:16,
                border:'2px solid #A8893D',
                borderRadius:8,
                background:'#FFF9E8'
              }}
            >
              <div style={{
                fontWeight:700,
                color:'#3B2A21',
                marginBottom:5
              }}>
                Record Supplier Response
              </div>

              <div style={{
                color:'#665A4D',
                fontSize:12,
                lineHeight:1.5,
                marginBottom:14
              }}>
                Use this only when the supplier responded
                by phone, WhatsApp, in person or another
                channel. The record is permanent.
              </div>

              <div className="form-group">
                <label className="form-label">
                  Supplier response
                </label>

                <select
                  className="form-control"
                  value={
                    manualResponseForm.response
                  }
                  onChange={event =>
                    setManualResponseForm({
                      ...manualResponseForm,
                      response:
                        event.target.value
                    })
                  }
                  disabled={
                    recordingManualResponse
                  }
                >
                  <option value="accepted">
                    Accepted
                  </option>
                  <option value="changes_requested">
                    Changes requested
                  </option>
                  <option value="declined">
                    Declined
                  </option>
                </select>
              </div>

              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(auto-fit,minmax(190px,1fr))',
                gap:12
              }}>
                <div className="form-group">
                  <label className="form-label">
                    Representative name *
                  </label>

                  <input
                    className="form-control"
                    value={
                      manualResponseForm
                        .responder_name
                    }
                    onChange={event =>
                      setManualResponseForm({
                        ...manualResponseForm,
                        responder_name:
                          event.target.value
                      })
                    }
                    disabled={
                      recordingManualResponse
                    }
                    maxLength={255}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Phone
                  </label>

                  <input
                    className="form-control"
                    value={
                      manualResponseForm
                        .responder_phone
                    }
                    onChange={event =>
                      setManualResponseForm({
                        ...manualResponseForm,
                        responder_phone:
                          event.target.value
                      })
                    }
                    disabled={
                      recordingManualResponse
                    }
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Email
                  </label>

                  <input
                    type="email"
                    className="form-control"
                    value={
                      manualResponseForm
                        .responder_email
                    }
                    onChange={event =>
                      setManualResponseForm({
                        ...manualResponseForm,
                        responder_email:
                          event.target.value
                      })
                    }
                    disabled={
                      recordingManualResponse
                    }
                    maxLength={320}
                  />
                </div>
              </div>

              {manualResponseForm.response !==
                'accepted' && (
                <div className="form-group">
                  <label className="form-label">
                    {manualResponseForm.response ===
                      'changes_requested'
                      ? 'Changes requested *'
                      : 'Reason for declining *'}
                  </label>

                  <textarea
                    className="form-control"
                    rows={3}
                    value={
                      manualResponseForm
                        .response_comment
                    }
                    onChange={event =>
                      setManualResponseForm({
                        ...manualResponseForm,
                        response_comment:
                          event.target.value
                      })
                    }
                    disabled={
                      recordingManualResponse
                    }
                    maxLength={4000}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  How and when was the response received? *
                </label>

                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Example: Confirmed by phone with Mrs Ade on 14 September 2026 at 10:30am."
                  value={
                    manualResponseForm
                      .evidence_note
                  }
                  onChange={event =>
                    setManualResponseForm({
                      ...manualResponseForm,
                      evidence_note:
                        event.target.value
                    })
                  }
                  disabled={
                    recordingManualResponse
                  }
                  maxLength={4000}
                  required
                />
              </div>

              <div style={{
                display:'flex',
                gap:8,
                flexWrap:'wrap'
              }}>
                <button
                  type="submit"
                  className="btn btn-gold"
                  disabled={
                    recordingManualResponse
                  }
                >
                  {recordingManualResponse
                    ? 'Recording...'
                    : 'Record Permanent Response'}
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={
                    closeManualResponse
                  }
                  disabled={
                    recordingManualResponse
                  }
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Actions */}
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {canApprove && po.status==='draft' && (
              <button className="btn btn-gold" onClick={handleApprove}>Approve PO</button>
            )}
            {canReceive && ['approved','partially_received'].includes(po.status) && (
              <div style={{flex:1}}>
                <button
                  className="btn btn-primary"
                  onClick={handleReceive}
                >
                  Receive into Inventory
                </button>
                <div
                  style={{
                    fontSize:11,
                    color:'#8A8A8A',
                    marginTop:6
                  }}
                >
                  Inventory will physically count each PO line. Only accepted quantities will enter stock.
                </div>
              </div>
            )}
            {canApprove && po.status==='draft' && (
              <button className="btn btn-outline" style={{color:'var(--unpaid)',borderColor:'var(--unpaid)'}}
                onClick={handleCancel}>Cancel PO</button>
            )}
            {po.status==='received' && po.bill_id && (
              <div className="alert alert-success" style={{flex:1,margin:0}}>
                Inventory confirmed all PO lines. The supplier bill is available in Bills & Expenses.
              </div>
            )}
            {po.status==='partially_received' && (
              <div className="alert alert-info" style={{flex:1,margin:0}}>
                Inventory has received part of this Purchase Order. Outstanding PO lines must still be counted and confirmed.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
